import { MessageSquare } from "lucide-react";
import { COLORS, SPACE, TYPE } from "../theme/index.js";
import type { ConversationSummary } from "./useConversations.js";

interface ChatHistorySidebarProps {
  conversations: ConversationSummary[];
  loading: boolean;
  activeId: string | null;
  onSelect: (id: string) => void;
}

function formatRelativeTime(dateStr: string | null): string {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const now = Date.now();
  const diff = now - date.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return date.toLocaleDateString();
}

export function ChatHistorySidebar({
  conversations,
  loading,
  activeId,
  onSelect,
}: ChatHistorySidebarProps) {
  return (
    <div
      data-testid="chat-history-sidebar"
      style={{
        flex: 1,
        overflowY: "auto",
        padding: `${SPACE[2]}px 0`,
      }}
    >
      {loading && (
        <div
          style={{
            padding: `${SPACE[8]}px ${SPACE[4]}px`,
            textAlign: "center",
            color: COLORS.textDim,
            fontSize: TYPE.size.sm,
          }}
        >
          Loading...
        </div>
      )}

      {!loading && conversations.length === 0 && (
        <div
          style={{
            padding: `${SPACE[8]}px ${SPACE[4]}px`,
            textAlign: "center",
            color: COLORS.textDim,
            fontSize: TYPE.size.sm,
          }}
        >
          No previous conversations
        </div>
      )}

      {conversations.map((conv) => {
        const isActive = conv.id === activeId;
        return (
          <button
            key={conv.id}
            type="button"
            data-testid="chat-history-item"
            onClick={() => onSelect(conv.id)}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "flex-start",
              gap: SPACE[2],
              padding: `${SPACE[2]}px ${SPACE[4]}px`,
              background: isActive ? COLORS.brandSoft : "transparent",
              border: "none",
              cursor: "pointer",
              textAlign: "left",
              borderRadius: 0,
              transition: "background 120ms ease",
            }}
          >
            <MessageSquare
              size={13}
              style={{
                color: isActive ? COLORS.brandText : COLORS.textDim,
                marginTop: 1,
                flexShrink: 0,
              }}
            />
            <div style={{ flex: 1, overflow: "hidden" }}>
              <div
                style={{
                  fontSize: TYPE.size.sm,
                  color: isActive ? COLORS.text : COLORS.textMuted,
                  fontWeight: isActive ? TYPE.weight.medium : TYPE.weight.normal,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {conv.title}
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: 2,
                }}
              >
                <span
                  style={{
                    fontSize: TYPE.size.xs,
                    color: COLORS.textDim,
                    textTransform: "capitalize",
                  }}
                >
                  {conv.assistant}
                </span>
                <span style={{ fontSize: TYPE.size.xs, color: COLORS.textGhost }}>
                  {formatRelativeTime(conv.last_message_at ?? conv.updated_at)}
                </span>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}
